import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PlusIcon, Trash2Icon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm, type DefaultValues, type FieldValues, type Path } from 'react-hook-form'
import { toast } from 'sonner'
import type { ZodType } from 'zod'

import { LoadingLines, LoadingRows } from '@/components/skeletons'
import { ApiError } from '@/api/client'
import { BlockerDialog, type FieldDef } from '@/components/entity-crud'
import { TreePicker } from '@/components/tree-picker'
import { TreeView } from '@/components/tree-view'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { EdaInfoFields, readEdaInfo } from '@/components/eda-info-fields'
import { cn } from '@/lib/utils'

import { AttachmentPanel } from '@/features/attachments/AttachmentPanel'
import type { OwnerType } from '@/features/attachments/api'

import {
  TREE_API,
  TREE_KINDS,
  flatten,
  treeKey,
  type TreeInput,
  type TreeKind,
  type TreeNode,
} from './api'

export interface TreeScreenProps<K extends TreeKind, Values extends FieldValues> {
  kind: K
  fields: FieldDef<Values>[]
  schema: ZodType<Values, Values>
  emptyValues: DefaultValues<Values>
  toValues: (detail: Record<string, unknown>) => DefaultValues<Values>
  /** Extra read-only facts shown above the form, e.g. a location's short code. */
  extra?: (detail: Record<string, unknown>) => React.ReactNode
  countLabel?: string
}

/** Which attachment owner each tree corresponds to. */
const OWNER_TYPES: Partial<Record<TreeKind, OwnerType>> = {
  categories: 'category',
  footprints: 'footprint',
  locations: 'storage_location',
}

/**
 * Tree on the left, editor for the selected node on the right. The same screen
 * serves all four trees; only the payload fields differ.
 */
export function TreeScreen<K extends TreeKind, Values extends TreeInput<K> & FieldValues>(
  props: TreeScreenProps<K, Values>,
) {
  const { kind } = props
  const info = TREE_KINDS[kind]
  const queryClient = useQueryClient()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [blockers, setBlockers] = useState<ApiError | null>(null)

  const tree = useQuery({ queryKey: treeKey(kind), queryFn: () => TREE_API[kind].tree() })

  const detail = useQuery({
    queryKey: ['tree', kind, selectedId],
    queryFn: () => TREE_API[kind].get(selectedId as string),
    enabled: selectedId !== null,
  })

  const nodes = tree.data ?? []
  const flat = flatten(nodes)
  const selected = flat.find((node) => node.id === selectedId) ?? null

  // A node that disappeared (deleted, or the tree reloaded) must not leave a
  // stale editor open.
  useEffect(() => {
    if (selectedId && flat.length > 0 && !flat.some((node) => node.id === selectedId)) {
      setSelectedId(null)
    }
  }, [flat, selectedId])

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['tree', kind] })
  }

  const remove = useMutation({
    mutationFn: (id: string) => TREE_API[kind].remove(id),
    onSuccess: async () => {
      setConfirmDelete(false)
      setSelectedId(null)
      toast.success(`${capitalise(info.label)} deleted`)
      await invalidate()
    },
    onError: (error) => {
      setConfirmDelete(false)
      if (error instanceof ApiError && error.status === 409) setBlockers(error)
      else toast.error(error instanceof Error ? error.message : 'Could not delete')
    },
  })

  const move = useMutation({
    mutationFn: ({ id, parentId }: { id: string; parentId: string | null }) =>
      TREE_API[kind].move(id, parentId),
    onSuccess: async () => {
      toast.success('Moved')
      await invalidate()
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : 'Could not move it'),
  })

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]">
      <div className="flex max-h-[70vh] flex-col gap-3">
        <Button onClick={() => setCreating(true)} className="w-full">
          <PlusIcon />
          New {info.label}
        </Button>

        {tree.isPending ? (
          <LoadingRows label={`Loading ${info.plural}`} />
        ) : tree.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Could not load the {info.plural}</AlertTitle>
            <AlertDescription>
              {tree.error instanceof Error ? tree.error.message : 'Unknown error'}
            </AlertDescription>
          </Alert>
        ) : (
          <TreeView
            nodes={nodes}
            selectedId={selectedId}
            onSelect={setSelectedId}
            countLabel={props.countLabel}
            emptyMessage={`No ${info.plural} yet.`}
          />
        )}
      </div>

      <div className="min-w-0">
        {selected === null ? (
          <div className="text-muted-foreground rounded-lg border border-dashed p-10 text-center text-sm">
            Choose {aOrAn(info.label)} on the left, or create one.
          </div>
        ) : detail.isPending ? (
          <LoadingLines label="Loading" lines={4} />
        ) : detail.data ? (
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="truncate text-lg font-semibold">{selected.name}</h2>
                <p className="text-muted-foreground text-sm">{selected.path}</p>
              </div>
              <Button
                variant="outline"
                onClick={() => setConfirmDelete(true)}
                className="text-destructive"
              >
                <Trash2Icon />
                Delete
              </Button>
            </div>

            {props.extra?.(detail.data)}

            <div className="flex flex-col gap-2">
              <Label htmlFor="tree-parent">Filed under</Label>
              <TreePicker
                id="tree-parent"
                label="Filed under"
                nodes={nodes}
                value={selected.parentId}
                noneLabel="Top level"
                excludeSubtreeOf={selected.id}
                onChange={(parentId) => move.mutate({ id: selected.id, parentId })}
              />
              <p className="text-muted-foreground text-xs">
                Its own children are not offered — nothing can be filed inside itself.
              </p>
            </div>

            <NodeForm
              key={selected.id}
              {...props}
              defaults={props.toValues(detail.data)}
              parentId={selected.parentId}
              onSubmit={async (values) => {
                await TREE_API[kind].update(selected.id, {
                  ...values,
                  parent_id: selected.parentId,
                })
                toast.success('Saved')
                await invalidate()
                await queryClient.invalidateQueries({ queryKey: ['tree', kind, selected.id] })
              }}
            />

            {OWNER_TYPES[kind] && (
              <div className="flex flex-col gap-3 border-t pt-5">
                <h3 className="text-sm font-semibold">Attachments</h3>
                <AttachmentPanel
                  key={selected.id}
                  ownerType={OWNER_TYPES[kind]}
                  ownerId={selected.id}
                  invalidate={[treeKey(kind)]}
                />
              </div>
            )}
          </div>
        ) : null}
      </div>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New {info.label}</DialogTitle>
            <DialogDescription>
              Leave “filed under” empty to create it at the top level.
            </DialogDescription>
          </DialogHeader>
          <CreateForm
            {...props}
            nodes={nodes}
            initialParentId={selectedId}
            onDone={async (id) => {
              setCreating(false)
              setSelectedId(id)
              await invalidate()
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete “{selected?.name}”?</DialogTitle>
            <DialogDescription>This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => selected && remove.mutate(selected.id)}
            >
              {remove.isPending && <Spinner />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BlockerDialog error={blockers} onClose={() => setBlockers(null)} />
    </div>
  )
}

function CreateForm<K extends TreeKind, Values extends TreeInput<K> & FieldValues>({
  kind,
  fields,
  schema,
  emptyValues,
  nodes,
  initialParentId,
  onDone,
}: TreeScreenProps<K, Values> & {
  nodes: TreeNode[]
  initialParentId: string | null
  onDone: (id: string) => Promise<void>
}) {
  const [parentId, setParentId] = useState<string | null>(initialParentId)
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: emptyValues })

  const create = useMutation({
    mutationFn: async (values: Values) => {
      const { data } = await TREE_API[kind].create({ ...values, parent_id: parentId })
      if (!data) throw new Error('The created node came back without an id')
      return data.id
    },
    onSuccess: async (id) => {
      toast.success(`${capitalise(TREE_KINDS[kind].label)} created`)
      await onDone(id)
    },
    onError: (error) => applyServerErrors(error, form),
  })

  return (
    <form
      onSubmit={form.handleSubmit((values) => create.mutate(values as Values))}
      className="flex flex-col gap-4"
      noValidate
    >
      <div className="flex flex-col gap-2">
        <Label htmlFor="new-parent">Filed under</Label>
        <TreePicker
          id="new-parent"
          label="Filed under"
          nodes={nodes}
          value={parentId}
          noneLabel="Top level"
          onChange={setParentId}
        />
      </div>
      <FormFields fields={fields} form={form} />
      <DialogFooter>
        <Button type="submit" disabled={create.isPending}>
          {create.isPending && <Spinner />}
          Create
        </Button>
      </DialogFooter>
    </form>
  )
}

function NodeForm<K extends TreeKind, Values extends TreeInput<K> & FieldValues>({
  fields,
  schema,
  defaults,
  onSubmit,
}: TreeScreenProps<K, Values> & {
  defaults: DefaultValues<Values>
  parentId: string | null
  onSubmit: (values: Values) => Promise<void>
}) {
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: defaults })

  const save = useMutation({
    mutationFn: (values: Values) => onSubmit(values),
    onSuccess: () => form.reset(form.getValues()),
    onError: (error) => applyServerErrors(error, form),
  })

  return (
    <form
      onSubmit={form.handleSubmit((values) => save.mutate(values as Values))}
      className="flex flex-col gap-4"
      noValidate
    >
      <FormFields fields={fields} form={form} />
      <div>
        <Button type="submit" disabled={save.isPending || !form.formState.isDirty}>
          {save.isPending && <Spinner />}
          Save changes
        </Button>
      </div>
    </form>
  )
}

function FormFields<Values extends FieldValues>({
  fields,
  form,
}: {
  fields: FieldDef<Values>[]
  form: ReturnType<typeof useForm<Values>>
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {fields.map((field) => {
        const error = form.formState.errors[field.name]?.message as string | undefined
        const id = `tree-field-${String(field.name)}`
        return (
          <div
            key={String(field.name)}
            className={cn(
              'flex flex-col gap-2',
              (field.wide || field.type === 'textarea' || field.type === 'eda') && 'sm:col-span-2',
            )}
          >
            {field.type === 'eda' ? (
              <>
                <Label>{field.label}</Label>
                {field.description && (
                  <p className="text-muted-foreground text-sm">{field.description}</p>
                )}
                <EdaInfoFields
                  idPrefix={id}
                  value={readEdaInfo(form.watch(field.name))}
                  onChange={(next) =>
                    form.setValue(field.name, next as never, { shouldDirty: true })
                  }
                />
              </>
            ) : field.type === 'checkbox' ? (
              <div className="flex items-center gap-2">
                <Checkbox
                  id={id}
                  checked={Boolean(form.watch(field.name))}
                  onCheckedChange={(checked) =>
                    form.setValue(field.name, (checked === true) as never, { shouldDirty: true })
                  }
                />
                <Label htmlFor={id}>{field.label}</Label>
              </div>
            ) : (
              <>
                <Label htmlFor={id}>{field.label}</Label>
                {field.type === 'textarea' ? (
                  <Textarea id={id} rows={3} {...form.register(field.name)} />
                ) : (
                  <Input
                    id={id}
                    placeholder={field.placeholder}
                    aria-invalid={error ? true : undefined}
                    {...form.register(field.name)}
                  />
                )}
              </>
            )}
            {field.description && !error && (
              <p className="text-muted-foreground text-xs">{field.description}</p>
            )}
            {error && <p className="text-destructive text-xs">{error}</p>}
          </div>
        )
      })}
    </div>
  )
}

function applyServerErrors<Values extends FieldValues>(
  error: unknown,
  form: ReturnType<typeof useForm<Values>>,
) {
  if (error instanceof ApiError && error.problem.errors?.length) {
    for (const problem of error.problem.errors) {
      form.setError(problem.field as Path<Values>, { message: problem.message })
    }
    return
  }
  toast.error(error instanceof Error ? error.message : 'Could not save')
}

function capitalise(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

function aOrAn(word: string) {
  return /^[aeiou]/i.test(word) ? `an ${word}` : `a ${word}`
}
